import {createCache, ExperimentsClient} from '@ezcater/experiments-js';
import {type IExperimentCache, type IExperimentData} from '@ezcater/experiments-js/dist/types';
import querystring, {type ParsedUrlQuery} from 'querystring';
import extractOverrides from '../util/extractOverrides';

interface CreateExperimentsClientOptions {
  query?: string | ParsedUrlQuery;
  shouldPreventExperimentOverrideExposureEvents?: boolean;
  trackingId?: string;
  initialState?: IExperimentData;
  host?: string;
}

interface LoadExperimentsProps {
  trackingId?: string;
  experiments?: string[];
}

export interface ExperimentsClientWithExtract extends ExperimentsClient {
  extract: () => IExperimentData;
  loadExperiments: (props?: LoadExperimentsProps) => Promise<IExperimentData>;
}

export const createExperimentsClient = ({
  query,
  shouldPreventExperimentOverrideExposureEvents = false,
  trackingId,
  initialState,
  host,
}: CreateExperimentsClientOptions = {}): ExperimentsClientWithExtract => {
  const experimentCache: IExperimentCache = createCache();

  if (initialState) {
    experimentCache.set(initialState);
  }

  const parsedQuery =
    typeof query === 'string' ? querystring.parse(query.replace(/^\?/, '')) : query;
  // isFallback is false in non-production environments to allow verification of experiment exposure events via overrides.
  const experimentOverrides = parsedQuery
    ? extractOverrides(parsedQuery, '__experiments', val => ({
        variant: val,
        isFallback: shouldPreventExperimentOverrideExposureEvents,
      }))
    : {};

  const experimentsClient = new ExperimentsClient({
    cache: experimentCache,
    host,
  });

  const loadExperiments = (props: LoadExperimentsProps = {}) => {
    return experimentsClient.get({
      trackingId: props.trackingId ?? trackingId ?? '',
      experiments: props.experiments ?? [],
    });
  };

  const extract = () => ({
    ...JSON.parse(experimentCache.toJSON()),
    ...experimentOverrides,
  });

  return Object.assign(experimentsClient, {
    loadExperiments,
    extract,
  }) as ExperimentsClientWithExtract;
};

export default createExperimentsClient;
